import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { marked } from "marked";
import { Clock, ChevronRight, ArrowUpRight } from "lucide-react";
import { fetchPost, API, CATEGORY_COLOR, categoryGradient } from "@/lib/blogApi";
import ReadingProgress from "@/components/blog/ReadingProgress";
import ShareBar from "@/components/blog/ShareBar";
import TableOfContents from "@/components/blog/TableOfContents";
import PdfDownloadGate from "@/components/blog/PdfDownloadGate";
import BlogCard from "@/components/blog/BlogCard";
import { useBlogTracker } from "@/hooks/useBlogTracker";
import { SITE_ORIGIN } from "@/lib/seoConfig";

marked.setOptions({ gfm: true, breaks: false });

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const articleRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(false);
    fetchPost(slug)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setErr(true); })
      .finally(() => { if (alive) setLoading(false); });
    window.scrollTo({ top: 0 });
    return () => { alive = false; };
  }, [slug]);

  const post = data?.post;
  const html = useMemo(() => (post ? marked.parse(post.content || "") : ""), [post]);

  // Track view + reading completion for admin analytics
  useBlogTracker(post?.slug, articleRef);

  // Inject SEO meta + JSON-LD into document head
  useEffect(() => {
    if (!post) return;
    const prevTitle = document.title;
    const baseTitle = post.metaTitle || post.title;
    document.title = /\|\s*Spiruva\s*$/i.test(baseTitle) ? baseTitle : `${baseTitle} | Spiruva`;

    // Absolute canonical URL — never uses preview host or query params.
    const canonicalHref = `${SITE_ORIGIN}${location.pathname}`;
    const ogImage = `${API}/blog/og/${post.slug}.png`;

    const tags = [
      { name: "description", content: post.metaDescription || post.excerpt },
      { name: "keywords", content: (post.tags || []).join(", ") },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: post.metaTitle || post.title },
      { property: "og:description", content: post.metaDescription || post.excerpt },
      { property: "og:type", content: "article" },
      { property: "og:url", content: canonicalHref },
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "article:published_time", content: post.publishedAt },
      { property: "article:author", content: post.author?.name },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: post.metaTitle || post.title },
      { name: "twitter:description", content: post.metaDescription || post.excerpt },
      { name: "twitter:image", content: ogImage },
    ];

    const created = [];
    tags.forEach((t) => {
      const sel = t.name ? `meta[name="${t.name}"]` : `meta[property="${t.property}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        if (t.name) el.setAttribute("name", t.name);
        if (t.property) el.setAttribute("property", t.property);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", t.content || "");
    });

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    let canonicalCreated = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      canonicalCreated = true;
    }
    canonical.setAttribute("href", canonicalHref);

    // JSON-LD
    const ld = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.metaDescription || post.excerpt,
      "image": ogImage,
      "author": { "@type": "Person", "name": post.author?.name, "jobTitle": post.author?.title },
      "publisher": {
        "@type": "Organization",
        "name": "SPIRUVA",
        "url": SITE_ORIGIN,
        "logo": { "@type": "ImageObject", "url": `${SITE_ORIGIN}/logo.png` },
      },
      "datePublished": post.publishedAt,
      "dateModified": post.updatedAt || post.publishedAt,
      "mainEntityOfPage": { "@type": "WebPage", "@id": canonicalHref },
      "articleSection": post.category,
      "keywords": (post.tags || []).join(", "),
    };
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.text = JSON.stringify(ld);
    ldScript.setAttribute("data-blog-ld", "1");
    document.head.appendChild(ldScript);

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
      if (canonicalCreated && canonical) canonical.remove();
      ldScript.remove();
    };
  }, [post, location.pathname]);

  if (loading) {
    return <div className="pt-[120px] container-wide pb-32 text-brand-black/60" data-testid="blogpost-loading">Loading article…</div>;
  }
  if (err || !post) {
    return (
      <div className="pt-[120px] container-wide pb-32" data-testid="blogpost-error">
        <p className="eyebrow mb-4">◦  Not found</p>
        <h1 className="font-display text-4xl text-brand-black">Article unavailable.</h1>
        <button onClick={() => navigate("/blog")} className="btn-primary mt-8">← Back to Blog</button>
      </div>
    );
  }

  const color = CATEGORY_COLOR[post.category] || CATEGORY_COLOR["Market Intelligence"];

  return (
    <div data-testid="page-blogpost" className="pt-[72px]">
      <ReadingProgress />

      {/* HEADER */}
      <header className="relative">
        <div
          className="h-64 md:h-80 w-full relative overflow-hidden"
          style={{ background: categoryGradient(post.category) }}
        >
          <div className="absolute inset-0 grain opacity-20 mix-blend-overlay" />
        </div>
        <div className="container-wide -mt-32 md:-mt-40 relative pb-12">
          <div className="bg-white border border-brand-green/15 p-8 md:p-12 max-w-4xl">
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-brand-black/50 mb-6">
              <Link to="/" className="hover:text-brand-teal">Home</Link>
              <ChevronRight size={12} />
              <Link to="/journal" className="hover:text-brand-teal">Journal</Link>
              <ChevronRight size={12} />
              <span className="truncate" style={{ color: color.hex }}>{post.category}</span>
            </nav>

            <div className="flex flex-wrap gap-3 items-center">
              <span
                className="font-mono text-[10px] uppercase tracking-[0.22em] px-2.5 py-1"
                style={{ backgroundColor: `${color.hex}15`, color: color.hex, border: `1px solid ${color.hex}40` }}
              >
                {post.category}
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-brand-black/55">
                <Clock size={12} /> {post.readingTime} min read
              </span>
            </div>

            <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-tight text-brand-black mt-6" data-testid="blogpost-title">
              {post.title}
            </h1>

            <div className="flex items-center gap-4 mt-8 pt-6 border-t border-brand-green/10">
              <div className="w-12 h-12 flex items-center justify-center font-display text-lg bg-brand-teal/10 text-brand-teal border border-brand-teal/30">
                {post.author?.initials}
              </div>
              <div>
                <p className="text-sm font-medium text-brand-black">{post.author?.name}</p>
                <p className="text-xs text-brand-black/55">{post.author?.title}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/45">Published</p>
                <p className="text-xs text-brand-black/75">{fmtDate(post.publishedAt)}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* BODY + SIDEBAR */}
      <section className="container-wide pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <article className="lg:col-span-8" data-testid="blogpost-body">
          <div
            ref={articleRef}
            className="prose-spiruva"
            dangerouslySetInnerHTML={{ __html: html }}
          />

          {post.tags?.length > 0 && (
            <div className="mt-12 pt-8 border-t border-brand-green/15">
              <p className="eyebrow mb-3">◦  Tagged</p>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <Link
                    key={t}
                    to={`/blog?tag=${encodeURIComponent(t)}`}
                    className="text-xs px-3 py-1.5 bg-brand-teal/8 border border-brand-teal/25 text-brand-teal hover:bg-brand-teal hover:text-brand-cream transition"
                  >
                    {t}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* End-of-article PDF CTA */}
          <div className="mt-12 bg-brand-black text-brand-cream p-8 md:p-10 relative overflow-hidden" data-testid="pdf-gate-end-article">
            <div className="absolute inset-0 grain opacity-20 mix-blend-overlay" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-8">
                <p className="eyebrow-light mb-2">◦  Premium Download</p>
                <h3 className="font-display text-3xl md:text-4xl leading-tight">
                  Get the typeset <span className="italic text-brand-gold">PDF report.</span>
                </h3>
                <p className="text-sm text-brand-cream/75 mt-3 leading-relaxed max-w-md">
                  Branded, beautifully formatted, sharable with your procurement, R&D, and formulation teams.
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <PdfDownloadGate post={post} variant="inline" />
              </div>
            </div>
          </div>

          {/* Author bio */}
          <div className="mt-12 bg-brand-green/5 border border-brand-green/15 p-8 flex items-start gap-6">
            <div className="w-16 h-16 shrink-0 flex items-center justify-center font-display text-2xl bg-brand-green text-brand-cream">
              {post.author?.initials}
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/45">About the Author</p>
              <h3 className="font-display text-2xl text-brand-black mt-1">{post.author?.name}</h3>
              <p className="text-sm text-brand-black/60">{post.author?.title}</p>
              <p className="text-sm text-brand-black/75 mt-3 leading-relaxed">
                Spiruva's editorial team includes co-founders and industry researchers covering the global phycocyanin and spirulina markets. We publish data-driven articles that help B2B buyers make better procurement decisions.
              </p>
            </div>
          </div>
        </article>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-[100px] space-y-6">
            <TableOfContents articleRef={articleRef} />

            <PdfDownloadGate post={post} variant="card" />

            <div className="bg-brand-green text-brand-cream p-6 relative overflow-hidden">
              <div className="absolute inset-0 grain opacity-20 mix-blend-overlay" />
              <div className="relative">
                <p className="eyebrow-light mb-2">◦  Pre-Launch Planning</p>
                <h3 className="font-display text-2xl leading-tight">Reserve Allocation</h3>
                <p className="text-sm text-brand-cream/80 mt-3 leading-relaxed">Discuss application fit, documentation, and a place in our July 2027 allocation.</p>
                <Link to="/reserve-allocation" data-testid="blogpost-cta-reserve" className="btn-ghost-light mt-5 text-xs !py-3 !px-5">
                  Start Inquiry <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>

            {data.related?.length > 0 && (
              <div>
                <p className="eyebrow mb-4">◦  Related Articles</p>
                <div className="space-y-4">
                  {data.related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/journal/${r.slug}`}
                      data-testid={`blog-related-${r.slug}`}
                      className="block bg-white border border-brand-green/15 p-4 hover:border-brand-gold transition"
                    >
                      <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: CATEGORY_COLOR[r.category]?.hex }}>
                        {r.category}
                      </p>
                      <h4 className="font-display text-base text-brand-black mt-1.5 leading-snug line-clamp-3">{r.title}</h4>
                      <p className="text-[11px] text-brand-black/50 mt-2 font-mono">{r.readingTime} min read</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>
      </section>

      <ShareBar post={post} articleRef={articleRef} />

      {/* RELATED READING STRIP — full-width, tag-overlap powered */}
      {data.related?.length > 0 && (
        <section
          data-testid="blog-related-strip"
          className="bg-brand-cream/40 border-t border-brand-green/15 py-20"
        >
          <div className="container-wide">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
              <div>
                <p className="eyebrow mb-2">◦  Continue Reading</p>
                <h2 className="font-display text-3xl md:text-4xl text-brand-black tracking-tight leading-tight max-w-2xl">
                  More for buyers who read <span className="italic text-brand-teal">this article.</span>
                </h2>
              </div>
              <Link
                to="/journal"
                data-testid="blog-related-strip-view-all"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-black/70 hover:text-brand-gold transition self-start md:self-auto"
              >
                Browse All Articles <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {data.related.slice(0, 3).map((r) => (
                <div key={r.id} data-testid={`blog-related-strip-${r.slug}`}>
                  <BlogCard post={r} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
